import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ContentSyncCheckbox,
  ContentSyncState,
  ContentSyncToolbar,
  useContentSync
} from "../components/admin/ContentSyncControls";

function AdminFrames({ apiBase, token }) {
  const navigate = useNavigate();
  const [frames, setFrames] = useState([]);
  const [graphs, setGraphs] = useState([]);
  const [filter, setFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [notice, setNotice] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ROWS_PER_PAGE = 25;

  useEffect(() => {
    let mounted = true;
    async function loadFrames() {
      setLoading(true);
      try {
        const [framesRes, graphsRes] = await Promise.all([
          fetch(`${apiBase}/api/admin/frames`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${apiBase}/api/admin/solution-graphs`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        if (!framesRes.ok || !graphsRes.ok) throw new Error("Failed to load frames");

        const [framesPayload, graphsPayload] = await Promise.all([
          framesRes.json(),
          graphsRes.json()
        ]);

        if (mounted) {
          setFrames(framesPayload.frames || []);
          setGraphs(graphsPayload.graphs || []);
        }
      } catch (err) {
        if (mounted) setNotice({ type: "error", text: err.message || "Failed to load frames" });
      } finally {
        if (mounted) setLoading(false);
      }
    }
    if (token) loadFrames();
    return () => {
      mounted = false;
    };
  }, [apiBase, token]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, typeFilter]);

  const graphCountByFrameId = useMemo(() => {
    return graphs.reduce((acc, item) => {
      const frameId = String(item?.frameId?._id || item?.frameId || "");
      if (!frameId) return acc;
      acc[frameId] = (acc[frameId] || 0) + 1;
      return acc;
    }, {});
  }, [graphs]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return frames.filter((f) => {
      const matchesText =
        !q ||
        String(f.chapterId || "").toLowerCase().includes(q) ||
        String(f.title || "").toLowerCase().includes(q) ||
        String(f.difficulty || "").toLowerCase().includes(q);
      const matchesType =
        typeFilter === "all" ||
        (typeFilter === "library" && f.isLibraryFrame) ||
        (typeFilter === "regular" && !f.isLibraryFrame);
      return matchesText && matchesType;
    });
  }, [frames, filter, typeFilter]);

  const syncItems = useMemo(
    () => frames.map((frame) => ({ contentType: "frame", contentId: String(frame._id) })),
    [frames]
  );
  const contentSync = useContentSync({ apiBase, token, items: syncItems });
  const visibleSyncItems = useMemo(
    () => filtered.map((frame) => ({ contentType: "frame", contentId: String(frame._id) })),
    [filtered]
  );

  const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const paginatedFrames = useMemo(() => {
    const start = (currentPage - 1) * ROWS_PER_PAGE;
    const end = start + ROWS_PER_PAGE;
    return filtered.slice(start, end);
  }, [filtered, currentPage, ROWS_PER_PAGE]);

  return (
    <div className="admin-panel">
      <div className="admin-toolbar">
        <input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Filter frames" />
        <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
          <option value="all">All Types</option>
          <option value="regular">Regular</option>
          <option value="library">Library</option>
        </select>
        {contentSync.canAuthorContent && (
          <>
            <button
              className="secondary-button"
              type="button"
              onClick={() => navigate("/admin/frames/new", { state: { isLibraryFrame: true } })}
            >
              New Library Frame
            </button>
            <button className="primary-button" type="button" onClick={() => navigate("/admin/frames/new")}>
              Create Frame
            </button>
          </>
        )}
      </div>

      <ContentSyncToolbar sync={contentSync} visibleItems={visibleSyncItems} />

      {notice.text && (
        <div className={`admin-notice ${notice.type === "error" ? "error" : "success"}`}>{notice.text}</div>
      )}

      {filtered.length > 0 && (
        <div className="pagination-controls">
          <button className="pagination-btn"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ← Prev
          </button>

          <span className="pagination-info">
            Page {currentPage} of {totalPages}
            &nbsp;·&nbsp;
            {filtered.length} rows total
          </span>

          <button className="pagination-btn"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      )}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              {contentSync.showSync && <th aria-label="Select for production sync" />}
              <th>S.No</th>
              <th>Type</th>
              <th>Chapter</th>
              <th>Frame Title</th>
              <th>Difficulty</th>
              <th>Order</th>
              <th>#Graphs</th>
              <th>Status</th>
              {contentSync.showSync && <th>Production</th>}
            </tr>
          </thead>
          <tbody>
            {paginatedFrames.map((frame, index) => {
              const syncItem = { contentType: "frame", contentId: String(frame._id) };
              return (
                <tr
                  key={frame._id}
                  className={contentSync.isSelected(syncItem) ? "selected" : ""}
                  onClick={() =>
                    navigate(`/admin/frames/${frame._id}`, {
                      state: { frame }
                    })
                  }
                >
                  {contentSync.showSync && (
                    <td onClick={(event) => event.stopPropagation()}>
                      <ContentSyncCheckbox
                        sync={contentSync}
                        item={syncItem}
                        label={`Select frame ${frame.title} for production sync`}
                      />
                    </td>
                  )}
                  <td>{(currentPage - 1) * ROWS_PER_PAGE + index + 1}</td>
                <td>
                  {frame.isLibraryFrame
                    ? <span className="solution-graph-library-badge">Library</span>
                    : <span style={{ color: "#6b7280", fontSize: 12 }}>Regular</span>}
                </td>
                <td>{frame.chapterId}</td>
                <td>{frame.title}</td>
                <td>{frame.difficulty}</td>
                <td>{frame.frameOrder || 1}</td>
                <td>{graphCountByFrameId[String(frame._id)] || 0}</td>
                <td>{frame.active === false ? "Inactive" : "Active"}</td>
                  {contentSync.showSync && (
                    <td><ContentSyncState sync={contentSync} item={syncItem} /></td>
                  )}
                </tr>
              );
            })}
            {loading && (
              <tr>
                <td colSpan={contentSync.showSync ? 10 : 8}>Loading frames...</td>
              </tr>
            )}
            {!filtered.length && !loading && (
              <tr>
                <td colSpan={contentSync.showSync ? 10 : 8}>No frames found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminFrames;
